import { Core } from "@faire/design-tokens";
import { VIEWPORT } from "@faire/help-center-utils/consts/eventEnum";
import { CustomerTypes } from "@faire/help-center-utils/enums/CustomerType";
import { useCustomerType } from "@faire/help-center-utils/hooks/useCustomerType";
import { localizePath } from "@faire/help-center-utils/lib/localization";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { Row } from "@faire/web--source/slate/Layout";
import {
  Typography,
  TypographyStyles,
} from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterRetailerBrandToggleHeaderClick } from "@faire/web-api--source/events/helpCenter/click/retailerBrandToggleHeader";
import brands from "@faire/web-api--source/routes/www/support/brands";
import retailers from "@faire/web-api--source/routes/www/support/retailers";
import React from "react";
import { useHistory } from "react-router-dom";
import styled, { css } from "styled-components";

export const Toggle: React.FC = () => {
  const { customerType } = useCustomerType();
  const history = useHistory();
  const retailerToken = getRetailerToken() ?? "";
  const brandToken = getBrandToken() ?? "";
  const { isMobile } = useViewport();

  const handleToggleClick = (customerType: CustomerTypes) => {
    trackHelpCenterRetailerBrandToggleHeaderClick(
      retailerToken,
      brandToken,
      isMobile ? VIEWPORT.WEB_MOBILE : VIEWPORT.WEB_DESKTOP,
      customerType
    );
    if (customerType === CustomerTypes.RETAILER) {
      history.push(localizePath(retailers()));
    } else {
      history.push(localizePath(brands()));
    }
  };

  return (
    <ToggleContainer>
      <ToggleSlider $active={customerType === CustomerTypes.RETAILER}>
        <Typography>
          {customerType === CustomerTypes.RETAILER ? (
            <StrictLocalMsg
              defaultMessage="Retailers"
              description={{
                text: "Toggle button for Retailers",

                img: "https://cdn.faire.com/fastly/d498b624bffa40b5c692e2ed97b5afd379deac1fc8ee290d3c3299096195ccc6.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Brands"
              description={{
                text: "Toggle button for Brands",

                img: "https://cdn.faire.com/fastly/d498b624bffa40b5c692e2ed97b5afd379deac1fc8ee290d3c3299096195ccc6.png",
              }}
            />
          )}
        </Typography>
      </ToggleSlider>
      <ToggleButton
        $active={customerType === CustomerTypes.RETAILER}
        onClick={() => handleToggleClick(CustomerTypes.RETAILER)}
      >
        <StrictLocalMsg
          defaultMessage="Retailers"
          description={{
            text: "Toggle button for Retailers",

            img: "https://cdn.faire.com/fastly/d498b624bffa40b5c692e2ed97b5afd379deac1fc8ee290d3c3299096195ccc6.png",
          }}
        />
      </ToggleButton>
      <ToggleButton
        $active={customerType === CustomerTypes.BRAND}
        onClick={() => handleToggleClick(CustomerTypes.BRAND)}
      >
        <StrictLocalMsg
          defaultMessage="Brands"
          description={{
            text: "Toggle button for Brands",

            img: "https://cdn.faire.com/fastly/d498b624bffa40b5c692e2ed97b5afd379deac1fc8ee290d3c3299096195ccc6.png",
          }}
        />
      </ToggleButton>
    </ToggleContainer>
  );
};

const ToggleContainer = styled(Row).attrs({
  align: "center",
  justify: "space-between",
})`
  background-color: ${Core.surface.inverse};
  border-radius: 50px;
  height: 45px;
  position: relative;
  border: 1px solid ${Core.border.muted};
`;

const ToggleButtonSharedStyle = css`
  padding: 10px 16px !important;
  border-radius: 50px;
  text-align: center;
  cursor: pointer;
`;

const ToggleButton = styled.div<{
  $active: boolean;
}>`
  ${ToggleButtonSharedStyle}
  ${TypographyStyles({ variant: "paragraphSansRegular" })};
  color: ${(props) =>
    props.$active ? Core.surface.inverse : Core.surface.default};
  transition: color 0.3s;
  flex: 1;
  z-index: 1;
`;

const ToggleSlider = styled.div<{
  $active: boolean;
}>`
  ${ToggleButtonSharedStyle}
  position: absolute;
  left: ${(props) => (props.$active ? "0px" : "calc(100% - 78px)")};
  background-color: ${Core.surface.default};
  transition: left 0.3s;
  z-index: 0;
  height: 45px;
`;
